'use client';

import { c as _c } from "react/compiler-runtime";
import { usePublicUserIdentity } from '@morfi/backend-sdk-v2/client';
import { UserAvatar, type UserAvatarSize } from '@/components/images/user-avatar';
import type { PartnerSocialLink } from '@/components/profile/user-name-display';
export type { PartnerSocialLink };
import { UserNameDisplay } from '@/components/profile/user-name-display';
import { cn } from '@/lib/ui/utils';

/**
 * Canonical shape passed to `<UserIdentity />`. Mirrors the fields the
 * centralised SDK identity hooks return — `connectedProviders` is always an
 * array, never optional, so badge rendering is consistent across surfaces.
 */
export type UserIdentityShape = {
  userId: string | null;
  walletAddress?: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  /**
   * Optional spec-071 NSFW flag for the avatar URL. Forwarded to
   * `<UserAvatar nsfw>` in the `with-avatar` variant. When the SDK
   * row doesn't surface it (older response shapes, mock data),
   * `null`/`undefined` are treated as "not flagged".
   */
  avatarNsfw?: boolean | null;
  /**
   * Optional avatar preferences (spec: avatar rasterise toggle). The
   * `rasterise` flag drives whether `<UserAvatar>` runs the photo
   * through the pattern-studio rasteriser. Forwarded to
   * `<UserAvatar rasterise>` in the `with-avatar` variant. When the
   * SDK row doesn't surface it, the wrapper defaults to `true` (matches
   * the DB DEFAULT for new users).
   */
  avatarPreferences?: {
    rasterise: boolean;
  } | null;
  /**
   * Provider list — the primitive filters to known badge providers (`x`,
   * `google`, `apple`) internally, so the wrapper accepts the looser
   * `provider: string` shape that backend responses naturally produce.
   * `username` may be null/undefined for Google/Apple (which key on email,
   * not a user-facing handle); `displayName` is optional and only used for
   * future-proofing.
   */
  connectedProviders: ReadonlyArray<{
    provider: string;
    username?: string | null | undefined;
    displayName?: string | null | undefined;
  }>;
};
type UserIdentityVariant = 'inline' | 'stack' | 'with-avatar';
type UserIdentitySize = 'sm' | 'md' | 'lg';
interface UserIdentityProps {
  /** Admin-managed partner names take precedence over the ordinary identity snapshot. */
  displayNameOverride?: string;
  user: UserIdentityShape;
  /**
   * Layout variant:
   * - `"inline"` (default): single line, name + handle truncate together.
   * - `"stack"`: name on first line, handle wraps below when space is tight.
   * - `"with-avatar"`: avatar + stacked name block in a flex row.
   */
  variant?: UserIdentityVariant;
  size?: UserIdentitySize;
  /**
   * Optional avatar size override. When omitted in `with-avatar` variant,
   * the wrapper picks a sensible default from `size`.
   */
  avatarSize?: UserAvatarSize;
  /**
   * Fallback text when both `displayName` and `handle` on `user` are absent.
   * Forwarded to the underlying primitive — used for surfaces that prefer a
   * context-specific fallback (wallet address, "View profile", an email)
   * over the default `@anonymous_morfian`.
   */
  fallback?: string;
  className?: string;
  /** Forwarded to UserNameDisplay — handle (`@…`) colour only. */
  handleClassName?: string;
  /** Forwarded to UserNameDisplay — display name span only. */
  nameClassName?: string;
  /** Forwarded to UserNameDisplay — inline X / Google / Apple badges beside the name. */
  showProviderBadges?: boolean;
  /** Hide the handle even when canonical profile data supplies one. */
  showHandle?: boolean;
  /** Partner creator — renders the handshake mark beside the name. */
  partner?: boolean;
  /** Partner's most prominent social account (icon + handle) beside the mark. */
  partnerSocial?: PartnerSocialLink | null | undefined;
}
const SIZE_TO_AVATAR_SIZE: Record<UserIdentitySize, UserAvatarSize> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg'
};
function getConnectedXHandle(providers: ReadonlyArray<{
  provider: string;
  username?: string | null | undefined;
  displayName?: string | null | undefined;
}>): string | null {
  const xProvider = providers.find(provider => provider.provider === 'x' || provider.provider === 'twitter');
  return xProvider?.username ?? null;
}

/**
 * Single-source-of-truth user identity wrapper.
 *
 * Composes `<UserAvatar>` and `<UserNameDisplay>` around a normalised
 * `UserIdentityShape`. Always forwards `connectedProviders` (defaulting to an
 * empty array) so badge rendering stays consistent — call sites should never
 * need to remember the prop.
 *
 * The wrapper is structural; animations live in the children.
 */
export function UserIdentity(t0) {
  const $ = _c(55);
  const {
    user,
    displayNameOverride,
    variant: t1,
    size: t2,
    avatarSize,
    fallback,
    className,
    handleClassName,
    nameClassName,
    showProviderBadges: t3,
    showHandle: t4,
    partner: t5,
    partnerSocial: t6
  } = t0;
  const variant = t1 === undefined ? "inline" : t1;
  const size = t2 === undefined ? "sm" : t2;
  const showProviderBadges = t3 === undefined ? true : t3;
  const showHandle = t4 === undefined ? true : t4;
  const partner = t5 === undefined ? false : t5;
  const partnerSocial = t6 === undefined ? null : t6;
  const canonicalUser = usePublicUserIdentity(user);
  let providers;
  let t7;
  if ($[0] !== canonicalUser.connectedProviders || $[1] !== canonicalUser.handle || $[2] !== showHandle) {
    providers = [...(canonicalUser.connectedProviders ?? [])];
    t7 = showHandle ? canonicalUser.handle ?? getConnectedXHandle(providers) : null;
    $[0] = canonicalUser.connectedProviders;
    $[1] = canonicalUser.handle;
    $[2] = showHandle;
    $[3] = providers;
    $[4] = t7;
  } else {
    providers = $[3];
    t7 = $[4];
  }
  const resolvedHandle = t7;
  const copyableMorfiHandle = showHandle ? canonicalUser.handle : null;
  let t8;
  if ($[5] !== fallback) {
    t8 = fallback !== undefined ? {
      fallback
    } : {};
    $[5] = fallback;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const fallbackProps = t8;
  let t9;
  if ($[7] !== handleClassName) {
    t9 = handleClassName !== undefined ? {
      handleClassName
    } : {};
    $[7] = handleClassName;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const handleClassProps = t9;
  let t10;
  if ($[9] !== nameClassName) {
    t10 = nameClassName !== undefined ? {
      nameClassName
    } : {};
    $[9] = nameClassName;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const nameClassProps = t10;
  let t11;
  if ($[11] !== showProviderBadges) {
    t11 = showProviderBadges === false ? {
      showProviderBadges: false as const
    } : {};
    $[11] = showProviderBadges;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const badgeProps = t11;
  let t12;
  if ($[13] !== partner || $[14] !== partnerSocial) {
    t12 = partner ? {
      partner: true as const,
      partnerSocial
    } : {};
    $[13] = partner;
    $[14] = partnerSocial;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const partnerProps = t12;
  if (variant === "with-avatar") {
    const resolvedAvatarSize = avatarSize ?? SIZE_TO_AVATAR_SIZE[size];
    let t13;
    if ($[16] !== className) {
      t13 = cn("inline-flex min-w-0 items-center gap-2", className);
      $[16] = className;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    const t14 = displayNameOverride ?? canonicalUser.displayName;
    const t15 = canonicalUser.avatarNsfw ?? false;
    const t16 = canonicalUser.avatarPreferences?.rasterise ?? false;
    let t17;
    if ($[18] !== canonicalUser.avatarUrl || $[19] !== canonicalUser.userId || $[20] !== resolvedAvatarSize || $[21] !== t14 || $[22] !== t15 || $[23] !== t16) {
      t17 = <UserAvatar userId={canonicalUser.userId} displayName={t14} avatarUrl={canonicalUser.avatarUrl} nsfw={t15} rasterise={t16} size={resolvedAvatarSize} />;
      $[18] = canonicalUser.avatarUrl;
      $[19] = canonicalUser.userId;
      $[20] = resolvedAvatarSize;
      $[21] = t14;
      $[22] = t15;
      $[23] = t16;
      $[24] = t17;
    } else {
      t17 = $[24];
    }
    const t18 = displayNameOverride ?? canonicalUser.displayName;
    let t19;
    if ($[25] !== badgeProps || $[26] !== copyableMorfiHandle || $[27] !== fallbackProps || $[28] !== handleClassProps || $[29] !== nameClassProps || $[30] !== partnerProps || $[31] !== providers || $[32] !== resolvedHandle || $[33] !== size || $[34] !== t18) {
      t19 = <UserNameDisplay displayName={t18} handle={resolvedHandle} copyableMorfiHandle={copyableMorfiHandle} connectedProviders={providers} size={size} layout="stack" {...badgeProps} {...partnerProps} {...fallbackProps} {...handleClassProps} {...nameClassProps} />;
      $[25] = badgeProps;
      $[26] = copyableMorfiHandle;
      $[27] = fallbackProps;
      $[28] = handleClassProps;
      $[29] = nameClassProps;
      $[30] = partnerProps;
      $[31] = providers;
      $[32] = resolvedHandle;
      $[33] = size;
      $[34] = t18;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    let t20;
    if ($[36] !== t13 || $[37] !== t17 || $[38] !== t19) {
      t20 = <span className={t13}>{t17}{t19}</span>;
      $[36] = t13;
      $[37] = t17;
      $[38] = t19;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    return t20;
  }
  const t13 = displayNameOverride ?? canonicalUser.displayName;
  let t14;
  if ($[40] !== className) {
    t14 = className !== undefined ? {
      className
    } : {};
    $[40] = className;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== badgeProps || $[43] !== copyableMorfiHandle || $[44] !== fallbackProps || $[45] !== handleClassProps || $[46] !== nameClassProps || $[47] !== partnerProps || $[48] !== providers || $[49] !== resolvedHandle || $[50] !== size || $[51] !== t13 || $[52] !== t14 || $[53] !== variant) {
    t15 = <UserNameDisplay displayName={t13} handle={resolvedHandle} copyableMorfiHandle={copyableMorfiHandle} connectedProviders={providers} size={size} layout={variant} {...badgeProps} {...partnerProps} {...fallbackProps} {...handleClassProps} {...nameClassProps} {...t14} />;
    $[42] = badgeProps;
    $[43] = copyableMorfiHandle;
    $[44] = fallbackProps;
    $[45] = handleClassProps;
    $[46] = nameClassProps;
    $[47] = partnerProps;
    $[48] = providers;
    $[49] = resolvedHandle;
    $[50] = size;
    $[51] = t13;
    $[52] = t14;
    $[53] = variant;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  return t15;
}